import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Camera, User, Mail, Shield, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DailyBriefingBar from '../components/DailyBriefingBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${API_URL}/api/auth/profile`, { name }, { withCredentials: true });
      updateUser({ name });
      setMessage('Profile updated');
    } catch {
      setMessage('Failed to update');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handlePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setMessage('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage('Image must be under 5MB');
      return;
    }

    setUploading(true);
    setMessage('');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API_URL}/api/auth/profile-pic`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ profile_pic: data.profile_pic });
      setMessage('Photo updated');
    } catch {
      setMessage('Failed to upload photo');
    } finally {
      setUploading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="profile-page">
      <DailyBriefingBar />

      <header className="bg-white" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 mb-4 transition-all duration-200"
            style={{ color: '#8B7355' }}
            data-testid="profile-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
          <h1 className="text-3xl font-light tracking-tight" style={{ color: '#2C2416' }}>Your Profile</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
        {message && (
          <div
            className="mb-6 p-3 rounded-sm text-sm text-center transition-all duration-300"
            style={{ backgroundColor: message.includes('Failed') ? '#FEF2F2' : '#F0FDF4', color: message.includes('Failed') ? '#991B1B' : '#166534', border: `1px solid ${message.includes('Failed') ? '#FECACA' : '#BBF7D0'}` }}
            data-testid="profile-message"
          >
            {message}
          </div>
        )}

        {/* Profile Picture */}
        <div className="bg-white border rounded-sm shadow-sm p-8 mb-6" style={{ borderColor: '#E5E1DB' }}>
          <div className="flex flex-col items-center">
            <div className="relative group cursor-pointer mb-4" onClick={() => fileInputRef.current?.click()}>
              {user?.profile_pic ? (
                <img
                  src={user.profile_pic}
                  alt="Profile"
                  className="w-28 h-28 rounded-full object-cover border-4"
                  style={{ borderColor: '#D4AF37' }}
                  data-testid="profile-pic-image"
                />
              ) : (
                <div
                  className="w-28 h-28 rounded-full flex items-center justify-center border-4 text-3xl font-light"
                  style={{ backgroundColor: '#F5F1EB', borderColor: '#D4AF37', color: '#8B7355' }}
                  data-testid="profile-pic-placeholder"
                >
                  {initials}
                </div>
              )}
              <div
                className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{ backgroundColor: 'rgba(44, 36, 22, 0.5)' }}
              >
                <Camera className="w-8 h-8 text-white" />
              </div>
              {uploading && (
                <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(44, 36, 22, 0.6)' }}>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePicUpload}
              className="hidden"
              data-testid="profile-pic-input"
            />
            <p className="text-sm" style={{ color: '#8B7355' }}>Click to upload photo</p>
          </div>
        </div>

        {/* Profile Details */}
        <div className="bg-white border rounded-sm shadow-sm p-8" style={{ borderColor: '#E5E1DB' }}>
          <h2 className="text-xl font-light mb-6" style={{ color: '#2C2416' }}>Account Details</h2>

          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>
                <User className="w-3.5 h-3.5" /> Full Name
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
                  style={{ borderColor: '#E5E1DB' }}
                  data-testid="profile-name-input"
                />
                <button
                  onClick={handleSaveName}
                  disabled={saving || name === user?.name}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium transition-all duration-200 disabled:opacity-40"
                  style={{ backgroundColor: '#D4AF37', color: 'white' }}
                  data-testid="profile-save-name-button"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>
                <Mail className="w-3.5 h-3.5" /> Email
              </label>
              <p className="px-4 py-2.5 rounded-sm font-mono" style={{ backgroundColor: '#FDFCFB', color: '#2C2416', border: '1px solid #E5E1DB' }} data-testid="profile-email">
                {user?.email}
              </p>
            </div>

            {/* Role (read-only) */}
            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>
                <Shield className="w-3.5 h-3.5" /> Role
              </label>
              <span
                className="inline-block px-4 py-1.5 rounded-full text-sm font-medium uppercase tracking-wider"
                style={{ backgroundColor: '#D4AF37', color: 'white' }}
                data-testid="profile-role"
              >
                {user?.role}
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
